<template>
	<div class="nnrc-survey-forms">
		<h2 class="content-block">
			<img src="@/assets/colpuno_big_transparent.png" class="header-image" v-if="showHeaderImage" />
			<div class="header-title">NNRC Survey</div>
		</h2>

		<DxPopup v-model:visible="submitError" @hidden="submitError = false" title="Error submitting survey"
			:shading="true" shadingColor="#ccca" :showTitle="true" :showCloseButton="true" :dragEnabled="false" :hideOnOutsideClick="true"
			max-width="400px" max-height="280px">
			<div class="submitsurvey-error-body">{{ submitErrorMsg }}</div>
		</DxPopup>

		<div class="content-block dx-card responsive-paddings application-form" v-if="existingForm.id">
			<div class="existing-warning">
				<i class="dx-icon dx-icon-check" style="font-size: 48px;color:green;"></i><br>
				<b>Thank you for submitting your survey form.</b><br>
				If necessary, we will send you an email with any further details.
			</div>
		</div>

		<div class="content-block dx-card responsive-paddings application-form" v-if="!existingForm.id">
			<form @submit.prevent="" enctype="multipart/form-data">
				<DxForm id="nnrc-survey-dxform" ref="formRef" label-mode="floating" :col-count="1" :form-data="applicationData" validation-group="surveyValidationGroup">
					<DxTabbedItem>
						<DxTabPanelOptions :swipe-enabled="false" :repaint-changes-only="true" :defer-rendering="false" :selected-index="tabSelectedIndex" @titleClick="formTabChanged" />
						
						<DxTab title="Introduction">
							<DxSimpleItem>
								<div class="simple-item-descriptive dx-card dx-theme-accent-as-border-color">
									<p class="simple-item-descriptive-title">Welcome!</p>
									<p>We – Brainhub and Colpuno - are thrilled to invite you, on behalf of your (former) university, to take part in this survey—designed specifically with you and your unique career journey in mind!</p>
									<p>This survey has two key goals:</p>
									<ul :class="{ 'list-tiny': isTiny }">
										<li>To understand the support you received—or are currently receiving—from your university in career planning and development,</li>
										<li>To identify the topics, digital tools, and support systems that have been or would be most helpful to you during your studies and as you transition into your nursing career.</li>
									</ul>
									<p>We aim to complete this survey by <u>January 31, 2025</u>, and we kindly ask that you participate within this period. The survey will take about <u>10-20 minutes</u> of your time.</p>
									<p>Your feedback will enable your university to learn from your experiences and refine its training programs. By participating, you’ll help shape future career support initiatives using the latest digital possibilities—ensuring that the next generation of nursing professionals is well-prepared for their careers.</p>
									<p>Thank you for joining us and your university on this journey! Your voice matters!</p>
								</div>
							</DxSimpleItem>
							<DxSimpleItem>
								<div class="simple-item-descriptive dx-card dx-theme-accent-as-border-color">
									<p class="simple-item-descriptive-title">Who are we?</p>
									<p :class="{ 'simple-item-descriptive-logoshow': true, 'tiny-logoshow': isTiny }">
										<img src="@/assets/brainhub_logo.png" style="width: 100px; height: 100px;" />
										<span><b>Brainhub Review Specialist</b>, a specialized entity based in Visayas, Republic of the Philippines, dedicated to conducting comprehensive reviews for nurses with a bachelor of science in nursing who are intending to take the Philippine Nursing Licensure Examination (PNLE), The National Council Licensure Examination (NCLEX) or other exams.</span>
									</p>
									<p :class="{ 'simple-item-descriptive-logoshow': true, 'tiny-logoshow': isTiny }">
										<img src="@/assets/colpuno_logo.png" style="width: 100px; height: 100px;" />
										<span><b>Colpuno</b> – <i>Empowering Filipino Nurses</i> – is building the first comprehensive digital marketplace tailored for Filipino nursing professionals. Leveraging artificial intelligence, Colpuno helps candidates identify their professional preferences, skills, and competencies, enabling them to present themselves effectively in the job market. Additionally, candidates receive personalized insights to help them position themselves optimally, ensuring they connect with the job opportunities that best fit their profile.</span>
									</p>
								</div>
							</DxSimpleItem>
							<DxButtonItem horizontal-alignment="right">
								<DxButtonOptions text="Next" type="default" :onClick="() => doFormTabChanged(1)" />
							</DxButtonItem>
						</DxTab>

						<DxTab title="General Information">
							<DxGroupItem caption="Personal details" :col-count="3">
								<DxSimpleItem data-field="first_name" :is-required="true" :label="{ text: 'First/Given name' }" />
								<DxSimpleItem data-field="middle_name" :is-required="false" />
								<DxSimpleItem data-field="last_name" :is-required="true" :label="{ text: 'Last/Family name' }" />
								<DxSimpleItem data-field="email" :is-required="true">
									<DxRequiredRule message="Email is required"/><DxEmailRule message="Email is invalid"/>
								</DxSimpleItem>
								<DxSimpleItem data-field="birthday" editor-type="dxDateBox" :editor-options="{ type: 'date', pickerType: 'native', max: Date(), dateSerializationFormat: 'yyyy-MM-dd' }" :is-required="true" />
								<DxSimpleItem data-field="gender" editor-type="dxSelectBox" :editor-options="genderListOptions" :is-required="true" />
								<DxSimpleItem data-field="country" editor-type="dxSelectBox" :editor-options="countryListOptions" :is-required="true" />
								<DxSimpleItem data-field="region" editor-type="dxSelectBox" :editor-options="regionListOptions" :is-required="true" :visible="showRegionList" />
							</DxGroupItem>
							<DxGroupItem caption="Educational background" :col-count="1">
								<DxSimpleItem data-field="nursing_school" editor-type="dxSelectBox" :editor-options="schoolListOptions" :label="{ text: 'Nursing School Attended' }" :is-required="true" :visible="!customSchoolName"/>
								<DxSimpleItem :col-span="3" css-class="group-hint-header" :visible="!customSchoolName">
									<i class="dx-icon dx-icon-info"></i>
									<span>Click/tap on the boxes and type to search for your school, or <a href="#" @click.prevent="customSchoolName = true">click here</a> to type your own school.</span>
								</DxSimpleItem>
								<DxSimpleItem data-field="nursing_school" :label="{ text: 'Nursing School Attended (type your own)' }" :is-required="true" :visible="customSchoolName"/>
								<DxSimpleItem data-field="pnle_years" :is-required="true" :label="{ text: 'PNLE Completion Year' }" editor-type="dxNumberBox" :editor-options="{ min: 1900, max: (new Date()).getFullYear(), showSpinButtons: true }" />
								<DxSimpleItem>
									<LanguageProficency :initial="applicationData.languages" validationGroup="surveyValidationGroup" @valueChanged="applicationData.languages = $event;" />
								</DxSimpleItem>
							</DxGroupItem>
							<DxGroupItem caption="Current Status">
								<DxSimpleItem data-field="current_status" editor-type="dxSelectBox" :label="{ text: 'My Status is...' }" :editor-options="currentStatusListOptions" :is-required="true" />
								<DxSimpleItem data-field="current_workplace" editor-type="dxSelectBox" :label="{ text: 'Workplace Country' }" :editor-options="currentWorkplaceListOptions" :is-required="true" :visible="applicationData.current_status == 'alumni'" />
							</DxGroupItem>
							<DxButtonItem horizontal-alignment="right">
								<DxButtonOptions text="Next" type="default" :onClick="() => doFormTabChanged(2)" />
							</DxButtonItem>
						</DxTab>

						<DxTab title="Your Education">
							<DxGroupItem caption="Your Education in Career Planning and Development">
								<DxSimpleItem data-field="counseling_importance" editor-type="dxSelectBox" :label="{ text: 'How important is/was personalized career counseling during your education?' }" :editor-options="counselingImportanceListOptions" :is-required="true" />
								<DxSimpleItem data-field="university_prepared" editor-type="dxSelectBox" :label="{ text: 'How well do/did you feel prepared for professional practice by your university education?' }" :editor-options="universityPreparedListOptions" :is-required="true" />
								<DxSimpleItem>
									<RatedTopics label="Which topics are/were part of your education, and how important are/were they to you?" validationGroup="surveyValidationGroup" :topics="educationTopics" :initial="applicationData.education_topics" @valueChanged="applicationData.education_topics = $event" />
								</DxSimpleItem>
								<DxSimpleItem>
									<RatedTopics label="Which of the following topics related to 'Working Abroad' are/were covered in your education, and how important are/were these topics to you?" validationGroup="surveyValidationGroup" :topics="workingAbroadTopics" :initial="applicationData.working_abroad" @valueChanged="applicationData.working_abroad = $event" />
								</DxSimpleItem>
								<DxSimpleItem>
									<RatedTopics label="Which digital tools did you have access to during your education, and how important were they or would they have been for your career development?" validationGroup="surveyValidationGroup" :topics="digitalToolsTopics" :initial="applicationData.digital_tools" @valueChanged="applicationData.digital_tools = $event" />
								</DxSimpleItem>
							</DxGroupItem>
							<DxButtonItem horizontal-alignment="right">
								<DxButtonOptions text="Next" type="default" :onClick="() => doFormTabChanged(3)" />
							</DxButtonItem>
						</DxTab>

						<DxTab title="Your Recommendations">
							<DxGroupItem caption="Your Recommendations to Universities for Future Training Programs in Career Planning and Development">
								<DxSimpleItem data-field="sim_interview_tool" editor-type="dxSelectBox" :label="{ text: 'Would you use a simulation tool for interview preparation to prepare for the application process?' }" :editor-options="simInterviewToolOptions" :is-required="true" />
								<DxSimpleItem data-field="e_learning_skills" editor-type="dxSelectBox" :label="{ text: 'Would you use e-learning platforms to acquire additional skills beyond your education?' }" :editor-options="eLearningSkillsOptions" :is-required="true" />
								<DxSimpleItem data-field="e_learning_topics" :label="{ text: 'Which topics would you prefer to see on an e-learning platform for nurses?' }" editor-type="dxTagBox" :editorOptions="eLearningTopicsOptions" :is-required="true" />
								<DxSimpleItem data-field="abroad_platform" editor-type="dxSelectBox" :label="{ text: 'If you are considering a career abroad, would you use a platform that provides information and support for recognition of your qualifications?' }" :editor-options="abroadPlatformOptions" :is-required="true" />
								<DxSimpleItem data-field="recognition_support" :label="{ text: 'What type of support would be most important to you for the international recognition of your qualifications?' }" editor-type="dxTagBox" :editorOptions="recognitionSupportOptions" :is-required="true" />
								<DxSimpleItem data-field="digital_elements" :label="{ text: 'What interactive, digital elements should universities provide in career planning and development training to enhance learning and ease the transition to employment?' }" editor-type="dxTagBox" :editorOptions="digitalElementsOptions" :is-required="true" />
							</DxGroupItem>
							<DxButtonItem horizontal-alignment="right">
								<DxButtonOptions text="Next" type="default" :onClick="() => doFormTabChanged(4)" />
							</DxButtonItem>
						</DxTab>

						<DxTab title="Interest in Planning Tools">
							<DxGroupItem caption="Interest in Digital Career Planning Tools">
								<DxSimpleItem data-field="platform_use" editor-type="dxSelectBox" :label="{ text: 'Would you use a digital platform for career planning and matching with employers?' }" :editor-options="platformUseOptions" :is-required="true" />
								<DxSimpleItem data-field="platform_features" editor-type="dxTagBox" :label="{ text: 'Which of the following features would be particularly useful for you?' }" :editorOptions="platformFeaturesOptions" :is-required="true" />
							</DxGroupItem>
							<DxButtonItem horizontal-alignment="right">
								<DxButtonOptions text="Next" type="default" :onClick="() => doFormTabChanged(5)" />
							</DxButtonItem>
						</DxTab>

						<DxTab title="Conclusion">
							<DxGroupItem caption="Do you have further recommendations on how digital tools can be integrated into nursing education to better prepare students for the job market?" :css-class="isTiny ? 'force-caption-tiny' : null">
								<DxSimpleItem data-field="recommendations" editor-type="dxTextArea" :editor-options="{ height: 200 }" :label="{ text: 'Further Recommendations' }" :is-required="false" />
							</DxGroupItem>
							<DxSimpleItem>
								<div class="simple-item-descriptive dx-card dx-theme-accent-as-border-color">
									<p class="simple-item-descriptive-title">Thank You for Your Participation!</p>
									<p style="font-style: italic; text-align: left;">Your responses are completely anonymous!</p>
									<p>Your insights have brought us closer to creating a platform that truly supports Filipino nurses.<br>Now, take the next step — join the exclusive COLPUNO Beta Community!</p>
									<p>With COLPUNO, you can:</p>
									<ul :class="{ 'list-tiny': isTiny }">
										<li>Generate your own digital homepage to professionally showcase your skills, experiences, and unique value.</li>
										<li>Define your strengths with personality assessments commonly used in manpower sourcing.</li>
										<li>Create and share your personalized Single Page Profile directly with employers to connect with the right opportunities.</li>
									</ul>
									<p>Take charge of your career path, connect with the right employers, and shape your future.</p>
									<p class="simple-item-descriptive-title">Register Now to Get Started!</p>
									<DxBox direction="row">
										<DxItem :ratio="0" :baseSize="40">
											<DxCheckBox v-model:value="applicationData.agree_beta" />
										</DxItem>
										<DxItem :ratio="1">
											Yes, I would like to participate in the COLPUNO Beta Community.
										</DxItem>
									</DxBox>
									<p style="font-style: italic; padding-left: 40px; margin-top: 5px;">If you'd prefer not to participate, please unselect this option.</p>
									<DxBox direction="row">
										<DxItem :ratio="0" :baseSize="40">
											<DxCheckBox v-model:value="applicationData.agree_privacy_policy" />
										</DxItem>
										<DxItem :ratio="1">
											<div><div>I have read and agree with the <a href="/privacy.html" target="_blank">Privacy Policy</a> regarding usage of my data.</div></div>
										</DxItem>
									</DxBox>
									<p>Thank you for your valuable contribution!</p>
								</div>
							</DxSimpleItem>
							<DxButtonItem :button-options="{ type: 'success', text: 'Submit Form', useSubmitBehavior: false, onClick: submitForm }" />
						</DxTab>

					</DxTabbedItem>
				</DxForm>
			</form>
			<DxLoadPanel :position="{ of: '.application-form' }" :shading="true" shadingColor="rgba(0,0,0,0.4)" v-model:visible="loadingVisible" />
		</div>
	</div>
</template>

<script setup>
/* eslint-disable */
import { DxForm, DxSimpleItem , DxGroupItem, DxButtonItem, DxButtonOptions, DxRequiredRule, DxEmailRule, DxTabbedItem, DxTab, DxTabPanelOptions } from "devextreme-vue/form";
import DxTextArea from 'devextreme-vue/text-area';			// eslint-disable-line no-unused-vars
import DxSelectBox from 'devextreme-vue/select-box';		// eslint-disable-line no-unused-vars
import { DxDateBox } from 'devextreme-vue/date-box';		// eslint-disable-line no-unused-vars
import DxTagBox from 'devextreme-vue/tag-box';				// eslint-disable-line no-unused-vars
import DxCheckBox from 'devextreme-vue/check-box';
import { DxLoadPanel } from 'devextreme-vue/load-panel';
import { DxBox, DxItem } from 'devextreme-vue/box';
import DxPopup from "devextreme-vue/popup"
import { inject, ref, computed, onMounted, onUnmounted, nextTick } from "vue";
import { useRouter } from 'vue-router';
import LanguageProficency from '../components/language-proficency.vue';
import RatedTopics from "../components/rated-topics.vue";
import to from 'await-to-js';
import DataSource from 'devextreme/data/data_source';
import notify from 'devextreme/ui/notify';
import { sizes as mediaSizes, subscribe as subscribeMediaQuery, unsubscribe as unsubscribeMediaQuery } from '../utils/media-query';
import countries from '../data/countries.js';
import regions from '../data/regions.js';
import schools from '../data/schools.js';
import appInfo from '../app-info.js';

const $http = inject('$http');
const isTiny = ref(mediaSizes()['screen-x-small']);
const router = useRouter();
const SURVEY_TYPE = 'nnrc14';
const SURVEY_STOREKEY = appInfo.localPrefix + 'Survey_' + SURVEY_TYPE;

const mediaQueryHandler = () => {
	isTiny.value = mediaSizes()['screen-x-small'];
};
onMounted(() => {
	subscribeMediaQuery(mediaQueryHandler);
});
onUnmounted(() => {
	unsubscribeMediaQuery(mediaQueryHandler);
});

const applicationData = ref({
	languages: [],
	education_topics: [], working_abroad: [], digital_tools: [],
	agree_beta: true,
	agree_privacy_policy: false,
});
const loadingVisible = ref(false);
const submitError = ref(false);
const submitErrorMsg = ref('');
const tabSelectedIndex = ref(0);
const formRef = ref(null);
const showHeaderImage = ref(true);

const formTabChanged = (event) => {
	doFormTabChanged(event.itemIndex);
};
const doFormTabChanged = (tabIndex) => {
	tabSelectedIndex.value = tabIndex;
	showHeaderImage.value = tabIndex == 0;
}

const existingForm = ref({ id: null, edit_secret: null });
const existingFormStore = window.localStorage.getItem(SURVEY_STOREKEY);
if (existingFormStore) {
	// Load existing ID and secret from local storage if possible.
	existingForm.value.id = existingFormStore.split(' ')[0];
	existingForm.value.edit_secret = existingFormStore.split(' ')[1];
}

const submitForm = async () => {
	// Manually switch to the tab with the first invalid field, since we don't highlight them in any other way.
	let validationResult = formRef.value.instance.validate();
	console.log(validationResult);
	if (!validationResult.isValid) {
		let switchToTab = null;
		validationResult.brokenRules.forEach(rule => {
			let ruleElement = rule.validator.element();
			let tabElement = ruleElement?.closest('.dx-item[role=tabpanel]') ?? null;
			let tabIndex = tabElement ? [...tabElement.parentElement.children].indexOf(tabElement) : null;
			if (tabIndex !== null && switchToTab == null) {
				switchToTab = tabIndex;
			}
		});
		if (switchToTab != null	) {
			// We must switch to the tab first, then validate, otherwise the validation elements are not visible!	
			doFormTabChanged(switchToTab);
			nextTick(() => {
				formRef.value.instance.validate();
			});
		}
		return;
	}
	
	if (!applicationData.value.agree_privacy_policy) {
		notify({ message: "Sorry, you must agree to the privacy policy before submitting this form!", position: 'center', shading: true, shadingColor: '#cccc' }, "error", 3000);
		return;
	}
	
	// Preprocess form data to remove any labels since we won't need to store them.
	// We clone the app data in preperation for preprocessing since in the event of an error, we want our original UI/forms to still work.
	let applicationDataClone = JSON.parse(JSON.stringify(applicationData.value));
	for (var i in applicationDataClone) {
		if (!Array.isArray(applicationDataClone[i]))
			continue;
		for (var j in applicationDataClone[i]) {
			delete applicationDataClone[i][j].label;
		}
	}
	
	console.log("Posting data", applicationDataClone);
	loadingVisible.value = true;
	let returnVal, err;
	let postUrl = '/survey';
	let formSubmission = {
		'_method': 'POST',
		'type': SURVEY_TYPE,
		'results': JSON.stringify(applicationDataClone),
	};
	[ err, returnVal ] = await to($http.post(postUrl, formSubmission));
	loadingVisible.value = false;
	if (err) {
		console.error([ err ]);
		let errorMsg = err.message ? err.message : 'Unknown error';
		if (err.response && err.response.statusText) {
			errorMsg += "\n" + err.response.statusText;
		}
		submitError.value = true;
		submitErrorMsg.value = "Error submitting your form:\n" + errorMsg;
		return;
	}
	console.log(returnVal);

	// When complete, we store the existing form ID and secret in local storage as an easy way to mark that the form has been completed (since this is unauthenticated).
	// Secrets are split by space since these are never generated.
	window.localStorage.setItem(SURVEY_STOREKEY, returnVal.data.id + ' ' + returnVal.data.edit_secret);
	notify({ message: "Your survey has been submitted successfully! Thank you for your time.", position: 'center', shading: true, shadingColor: '#cccc' }, "success", 3000);
	setTimeout(() => {
		router.go(0);
	}, 3000);
}

// General Information Tab
const genderListOptions = { dataSource: [ 'Male', 'Female', 'Diverse' ] };
const countriesData = new DataSource({ store: countries, pageSize: 10, paginate: true, sort: (v) => v.code == 'PH' ? '!' : v.name.toLowerCase() });
const countryListOptions = { dataSource: countriesData, valueExpr: 'code', displayExpr: 'name', searchEnabled: true };
const regionListOptions = { dataSource: regions.filter(r => r.code.startsWith('PH-')), valueExpr: 'code', displayExpr: 'name', searchEnabled: true };
const showRegionList = computed(() => applicationData.value.country == 'PH');
const schoolsData = new DataSource({ store: schools, sort: 'title', pageSize: 10, paginate: true });
const schoolListOptions = { dataSource: schoolsData, valueExpr: 'slug', displayExpr: 'title', searchEnabled: true,  };
const customSchoolName = ref(false);
const currentStatusListOptions = { dataSource: [ { id: 'current', name: 'Current Student' }, { id: 'alumni', name: 'Former student/Alumnus' } ], valueExpr: 'id', displayExpr: 'name' };
const countriesDataClone = new DataSource({ store: countries, pageSize: 10, paginate: true, sort: (v) => v.code == 'PH' ? '!' : v.name.toLowerCase() });
const currentWorkplaceListOptions = { dataSource: countriesDataClone, valueExpr: 'code', displayExpr: 'name', searchEnabled: true };	// NB: Needs to use clone of this data source otherwise search "carries over" from previous - very confusing for users using the filter!

// Your Education Tab
const counselingImportanceListOptions = { grouped: true, dataSource: [
	{ key: "How important is/was personalized career counseling during your education?", items: [
		{ id: 'very-important', name: 'Very important' },
		{ id: 'important', name: 'Important' },
		{ id: 'less-important', name: 'Less important' },
		{ id: 'unimportant', name: 'Unimportant' }
	]},
], valueExpr: 'id', displayExpr: 'name' };
const universityPreparedListOptions = { grouped: true, dataSource: [
	{ key: "How prepared do you feel for the job market after your education?", items: [
		{ id: 'very-well', name: 'Very well prepared' },
		{ id: 'well', name: 'Well prepared' }, 
		{ id: 'less-well', name: 'Less well prepared' },
		{ id: 'not', name: 'Not prepared' }
	]},
], valueExpr: 'id', displayExpr: 'name' };
const educationTopics = [
    { code: 'career-planning', name: 'Career planning and counseling' },
    { code: 'application-strategies', name: 'Application strategies and techniques' },
    { code: 'international-career', name: 'International career planning and opportunities, including general introduction to recognition procedures in target countries' },
    { code: 'job-search-tools', name: 'Use of job search tools' },
    { code: 'job-search-strategies', name: 'Job search strategies and application process' },
    { code: 'specialization', name: 'Specialization options in nursing' },
    { code: 'financial-planning', name: 'Financial planning and contract negotiation' },
    { code: 'professional-networks', name: 'Building professional networks' },
    { code: 'other', name: 'Other' }
];
const workingAbroadTopics = [
    { code: 'country-comparison', name: 'General country comparison' },
    { code: 'cultural-awareness', name: 'Cultural awareness' },
    { code: 'professional-standards', name: 'Professional standards and practices' },
    { code: 'language-preparation', name: 'Language preparation' },
    { code: 'legal-frameworks', name: 'Legal frameworks' },
    { code: 'practical-life', name: 'Practical life experiences' },
    { code: 'psychosocial-support', name: 'Psychosocial support' },
    { code: 'ethics', name: 'Ethics and professional ethics' },
    { code: 'financial-planning', name: 'Financial planning' }
];
const digitalToolsTopics = [
    { code: 'job-boards', name: 'Online job boards' },
    { code: 'career-planning-tools', name: 'Career planning tools (e.g., strengths/weaknesses analysis, awareness check)' },
    { code: 'recognition-test-prep', name: 'E-learning platforms for test preparation for foreign recognition procedures' },
    { code: 'language-test-prep', name: 'E-learning platforms for language test preparation' },
    { code: 'training-courses', name: 'E-learning platforms for further training courses' },
    { code: 'digital-portfolios', name: 'Digital portfolios to showcase skills' },
    { code: 'networking-platforms', name: 'Networking platforms for alumni and employer contacts' },
    { code: 'country-info-tools', name: 'Information about target countries and country comparison tools' },
    { code: 'other', name: 'Other' }
]

// Your Recommendations Tab
const simInterviewToolOptions = { grouped: true, dataSource: [
	{ key: "Would you use a simulation tool for interview preparation to prepare for the application process?", items: [
		{ id: 'yes-definitely', name: 'Yes, definitely' },
		{ id: 'probably-yes', name: 'Probably yes' },
		{ id: 'probably-not', name: 'Probably not' },
		{ id: 'no', name: 'No' }
	]},
], valueExpr: 'id', displayExpr: 'name' };
const eLearningSkillsOptions = { grouped: true, dataSource: [
	{ key: "Would you use e-learning platforms to acquire additional skills beyond your education?", items: [
		{ id: 'very-interested', name: 'Yes, very interested' },
		{ id: 'interested', name: 'Yes, interested' },
		{ id: 'probably-not', name: 'Probably not' },
		{ id: 'not-interested', name: 'No, not interested' }
	]},
], valueExpr: 'id', displayExpr: 'name' };
const eLearningTopics = [
	{ key: "Which topics would you prefer to see on an e-learning platform for nurses?", items: [
		{ code: 'time-management', name: 'Time management and organization in nursing practice' },
		{ code: 'communication', name: 'Patient communication and conflict management' },
		{ code: 'technical', name: 'Technical handling of medical equipment and software' },
		{ code: 'leadership', name: 'Leadership skills and teamwork' },
		{ code: 'specialized', name: 'Specialized training (e.g., intensive care, emergency nursing)' },
		{ code: 'languages', name: 'Learning foreign languages' }
	]},
];
const eLearningTopicsOptions = { dataSource: eLearningTopics, valueExpr: 'code', displayExpr: 'name', grouped: true };
const abroadPlatformOptions = { grouped: true, dataSource: [
	{ key: "If you are considering a career abroad, would you use a platform that provides information and support for recognition of your qualifications?", items: [
		{ id: 'yes-definitely', name: 'Yes, definitely' },
		{ id: 'probably-yes', name: 'Probably yes' },
		{ id: 'probably-not', name: 'Probably not' },
		{ id: 'no', name: 'No' }
	]},
], valueExpr: 'id', displayExpr: 'name' };
const recognitionSupportTopics = [
	{ key: "What type of support would be most important to you for the international recognition of your qualifications?", items: [
		{ code: 'overview-recognition', name: 'Overview of recognition procedures and requirements by country' },
		{ code: 'step-by-step-guides', name: 'Step-by-step guides for the application process' },
		{ code: 'contacts-international', name: 'Contacts with international healthcare institutions' },
		{ code: 'language-courses', name: 'Information about language courses and cultural adaptation' }
	]},
];
const recognitionSupportOptions = { dataSource: recognitionSupportTopics, valueExpr: 'code', displayExpr: 'name', grouped: true };
const digitalElementsTopics = [
	{ key: "What interactive, digital elements should universities provide in career planning and development training to enhance learning and ease the transition to employment?", items: [
		{ code: 'interactive-quizzes', name: 'Interactive quizzes for understanding and reinforcing learning' },
		{ code: 'case-studies', name: 'Case studies to apply theoretical knowledge to practical situations' },
		{ code: 'videos', name: 'Videos illustrating concepts and real-life scenarios' },
		{ code: 'hands-on-practice', name: 'Hands-on practice in a safe environment' },
		{ code: 'discussion-forums', name: 'Discussion forums and peer learning for experience sharing and collaborative learning' },
		{ code: 'additional-materials', name: 'Additional materials for in-depth learning as a resource library' }
	]},
];
const digitalElementsOptions = { dataSource: digitalElementsTopics, valueExpr: 'code', displayExpr: 'name', grouped: true };

// Interest in Planning Tools Tab
const platformUseOptions = { grouped: true, dataSource: [
	{ key: "Would you use a digital platform for career planning and matching with employers?", items: [
		{ id: 'yes-definitely', name: 'Yes, definitely' },
		{ id: 'probably-yes', name: 'Probably yes' },
		{ id: 'probably-not', name: 'Probably not' },
		{ id: 'no', name: 'No' }
	]},
], valueExpr: 'id', displayExpr: 'name' };
const platformFeaturesOptions = { grouped: true, dataSource: [
	{ key: "Which of the following features would be particularly useful for you?", items: [
		{ code: 'digital-portfolio', name: 'Creating a digital portfolio to showcase skills and experience' },
		{ code: 'personality-tests', name: 'Personality tests to better understand strengths and weaknesses' },
		{ code: 'preferred-employer', name: 'Features to find my preferred employer' },
		{ code: 'employer-platform', name: 'Access to a platform connecting me with national and international employers with job offers based on my current skills' },
		{ code: 'recruiting-agencies', name: 'Access to required recruiting agencies and their international job offers' },
		{ code: 'interview-simulation', name: 'Tools for interview and application scenario simulation' },
		{ code: 'online-career-counseling', name: 'Online/digital career counseling based on my interests and skills' },
		{ code: 'e-learning-modules', name: 'E-learning modules for developing specific professional skills' },
		{ code: 'general-information', name: 'General and job market-specific information about my target country' },
		{ code: 'intl-qual-recog', name: 'Support for international qualification recognition' }
	]},
], valueExpr: 'code', displayExpr: 'name' };

</script>

<style lang="scss">
.nnrc-survey-forms {
	.header-image {
		width: 100%;
		max-width: 1200px;
		max-height: 150px;
		object-fit: contain;
	}
	.header-title {
		text-align: center;
		text-transform: uppercase;
		width: 100%; max-width: 1200px;
		border-bottom: 4px double black;
	}
	.header-subtitle {
		text-align: center;
		width: 100%; max-width: 1200px;
		font-size: 20px;
	}
	.application-form {
		max-width: 1200px;
	}
	.simple-item-descriptive {
		padding: 0 20px;
		border-left: 6px solid;
		text-align: justify;
	}
	.simple-item-descriptive p+p {
		margin-top: 0;
	}
	.simple-item-descriptive-title {
		font-weight: bold;
		font-size: 1.2em;
	}
	.submitsurvey-error-body {
		white-space: pre-line;
		border-left: 2px solid red;
		padding-left: 15px;
	}
	.existing-warning {
		text-align: center;
	}
	.group-hint-header {
		font-style: italic;
		padding: 0 !important;
		span {
			vertical-align: middle;
		}
		i {
			vertical-align: middle;
			padding-right: 5px;
		}
	}
	.simple-item-descriptive-logoshow {
		display: flex;
		align-items: center;
		column-gap: 15px;
		&.tiny-logoshow {
			flex-direction: column;
			row-gap: 10px;
		}
	}
	.list-tiny {
		padding-inline-start: 15px;
	}
	.force-caption-tiny .dx-form-group-caption {
		font-weight: 500;
		font-size: 1.25em;
	}
}
</style>
